import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, ArrowLeft, X } from "lucide-react";
import api from "@/lib/api";
import { productImageSrc, FALLBACK_IMG } from "@/lib/site";

const CATEGORY_OPTIONS = [
  "Wardrobes & Cupboards",
  "Beds",
  "Bedroom Furniture",
  "TV Units",
  "Tables",
  "Dressing Tables",
  "Sofa Beds",
  "Custom Furniture",
];

const EMPTY_FORM = { name: "", category: CATEGORY_OPTIONS[0], price: "", mrp: "", description: "", details: "", wood: "", dimensions: "", finish: "", tag: "", image: "" };

const inputCls =
  "w-full rounded-lg border border-[#E7DFD5] bg-white px-3.5 py-2.5 text-sm text-walnut placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brass/60";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [files, setFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});

  useEffect(() => {
    const token = localStorage.getItem("jk_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); loadProducts(); })
      .catch(() => localStorage.removeItem("jk_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("jk_admin_token", data.token);
      setUser(data.user);
      loadProducts();
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("jk_admin_token");
    setUser(null);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFiles([]);
    setExistingImages([]);
    setShowForm(false);
  };

  const startEdit = (p) => {
    setForm({
      name: p.name, category: p.category, price: p.price, mrp: p.mrp || "",
      description: p.description || "", details: p.details || "",
      tag: p.tag || "", image: p.image || "",
      wood: p.wood || "", dimensions: p.dimensions || "", finish: p.finish || "",
    });
    setExistingImages(p.images?.length ? p.images : p.image ? [p.image] : []);
    setEditingId(p.id);
    setFiles([]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const images = [...existingImages];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        images.push(data.path);
      }
      const url = form.image.startsWith("http") ? form.image.trim() : "";
      if (url && !images.includes(url)) images.push(url);
      const payload = { ...form, images, image: images[0] || "" };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      resetForm();
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      loadProducts();
    } catch {
      toast.error("Could not delete product");
    }
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-sand text-sm text-stone-500">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand px-5">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm rounded-2xl border border-[#E7DFD5] bg-white p-8 shadow-sm">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-teak">JK Furniture</p>
          <h1 className="mt-2 font-serif text-2xl text-walnut">Owner Login</h1>
          <input
            data-testid="admin-login-email"
            type="email" required placeholder="Email"
            value={loginForm.email}
            onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
            className={`${inputCls} mt-6`}
          />
          <input
            data-testid="admin-login-password"
            type="password" required placeholder="Password"
            value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            className={`${inputCls} mt-3`}
          />
          {loginError && <p data-testid="admin-login-error" className="mt-3 text-xs text-red-600">{loginError}</p>}
          <button data-testid="admin-login-submit" type="submit" className="mt-5 w-full rounded-full bg-walnut py-3 text-sm font-semibold text-sand hover:bg-noir transition-colors">
            Sign In
          </button>
          <a href="/" className="mt-4 flex items-center justify-center gap-1 text-xs text-stone-500 hover:text-walnut">
            <ArrowLeft size={13} /> Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <header className="sticky top-0 z-40 border-b border-[#E7DFD5] bg-[#FBF8F5]/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <p className="font-serif text-lg text-walnut">JK Furniture — <span className="italic text-teak">Product Manager</span></p>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="text-xs font-medium text-stone-500 hover:text-walnut">View Site</a>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-[#E7DFD5] px-4 py-2 text-xs font-semibold text-walnut hover:bg-sandsubtle">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-walnut">Your Products ({products.length})</h2>
          <button
            data-testid="admin-add-product-button"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="inline-flex items-center gap-2 rounded-full bg-walnut px-5 py-2.5 text-sm font-semibold text-sand hover:bg-noir transition-colors"
          >
            <Plus size={15} /> Add Product
          </button>
        </div>

        {showForm && (
          <form data-testid="admin-product-form" onSubmit={save} className="mt-6 rounded-2xl border border-[#E7DFD5] bg-white p-6 sm:p-8">
            <p className="font-serif text-lg text-walnut">{editingId ? "Edit Product" : "New Product"}</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="admin-input-name" required placeholder="Product name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
              <select data-testid="admin-select-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls}>
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="admin-input-price" required placeholder="Selling price (e.g. ₹9,099)" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className={inputCls} />
              <input data-testid="admin-input-mrp" placeholder="MRP (optional, shown struck-through)" value={form.mrp} onChange={(e) => setForm((f) => ({ ...f, mrp: e.target.value }))} className={inputCls} />
              <input data-testid="admin-input-tag" placeholder="Tag (optional, e.g. Bestseller)" value={form.tag} onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))} className={inputCls} />
            </div>
            <textarea data-testid="admin-input-description" rows={2} placeholder="Short description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} mt-4`} />
            <input data-testid="admin-input-details" placeholder="Extra specs (optional, shown in the detail popup)" value={form.details} onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))} className={`${inputCls} mt-4`} />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input data-testid="admin-input-dimensions" placeholder="Dimensions (e.g. 78″L x 72″W x 42″H)" value={form.dimensions} onChange={(e) => setForm((f) => ({ ...f, dimensions: e.target.value }))} className={inputCls} />
              <input data-testid="admin-input-wood" placeholder="Wood (e.g. Solid Teak)" value={form.wood} onChange={(e) => setForm((f) => ({ ...f, wood: e.target.value }))} className={inputCls} />
              <input data-testid="admin-input-finish" placeholder="Finish (e.g. Natural Matte)" value={form.finish} onChange={(e) => setForm((f) => ({ ...f, finish: e.target.value }))} className={inputCls} />
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-stone-500">Upload photos from your phone/computer (pick several for a gallery)</label>
                <input
                  data-testid="admin-input-image-file"
                  type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  className="w-full text-sm text-stone-600 file:mr-3 file:rounded-full file:border-0 file:bg-sandsubtle file:px-4 file:py-2 file:text-xs file:font-semibold file:text-walnut hover:file:bg-brasslight"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-stone-500">…or paste an image link</label>
                <input data-testid="admin-input-image-url" placeholder="https://…" value={form.image.startsWith("http") ? form.image : ""} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} className={inputCls} />
              </div>
            </div>

            {(existingImages.length > 0 || files.length > 0) && (
              <div data-testid="admin-gallery-preview" className="mt-4 flex flex-wrap gap-3">
                {existingImages.map((img, i) => (
                  <div key={`${img}-${i}`} className="relative">
                    <img
                      src={productImageSrc(img)}
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                      alt={`Product photo ${i + 1}`}
                      className="h-28 w-28 rounded-xl border border-[#E7DFD5] object-cover"
                    />
                    <button
                      type="button"
                      data-testid={`admin-remove-image-${i}`}
                      onClick={() => setExistingImages((arr) => arr.filter((_, idx) => idx !== i))}
                      aria-label={`Remove photo ${i + 1}`}
                      className="absolute -right-2 -top-2 rounded-full bg-noir p-1 text-sand hover:bg-red-600 transition-colors"
                    >
                      <X size={12} />
                    </button>
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 rounded bg-noir/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brasslight">Cover</span>
                    )}
                  </div>
                ))}
                {files.map((f, i) => (
                  <img
                    key={`new-${i}`}
                    src={URL.createObjectURL(f)}
                    alt={`New upload ${i + 1}`}
                    className="h-28 w-28 rounded-xl border border-dashed border-brass object-cover opacity-80"
                  />
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-stone-500">The first photo becomes the cover image shown in the shop grid.</p>

            <div className="mt-6 flex gap-3">
              <button data-testid="admin-save-product-button" type="submit" disabled={saving} className="rounded-full bg-walnut px-6 py-2.5 text-sm font-semibold text-sand hover:bg-noir disabled:opacity-50 transition-colors">
                {saving ? "Saving…" : editingId ? "Save Changes" : "Add Product"}
              </button>
              <button type="button" onClick={resetForm} className="rounded-full border border-[#E7DFD5] px-6 py-2.5 text-sm font-semibold text-stone-600 hover:bg-sandsubtle">
                Cancel
              </button>
            </div>
          </form>
        )}

        <div data-testid="admin-product-list" className="mt-8 grid grid-cols-1 gap-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-4 rounded-xl border border-[#E7DFD5] bg-white p-4">
              <img
                src={productImageSrc(p.image)}
                onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                alt={p.name}
                className="h-16 w-16 shrink-0 rounded-lg object-cover bg-sandsubtle"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-walnut">{p.name}</p>
                <p className="truncate text-xs text-stone-500">{p.category} • {p.price}</p>
              </div>
              <button data-testid={`admin-edit-${p.id}`} onClick={() => startEdit(p)} className="rounded-full border border-[#E7DFD5] p-2.5 text-stone-600 hover:bg-sandsubtle" aria-label={`Edit ${p.name}`}>
                <Pencil size={15} />
              </button>
              <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p)} className="rounded-full border border-[#E7DFD5] p-2.5 text-red-500 hover:bg-red-50" aria-label={`Delete ${p.name}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {products.length === 0 && (
            <p className="rounded-xl border border-dashed border-[#E7DFD5] bg-white p-8 text-center text-sm text-stone-500">
              No products yet — click "Add Product" to list your first piece.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
